import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Card, CardMedia, Modal } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import Loading from '../components/Loading';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wrap images array in useMemo to prevent re-creation on each render
  const images = useMemo(
    () => [
      {
        title: 'Mirmire team',
        src: '/gallery/group.jpg',
        category: 'Team',
      },
      {
        title: 'Happy faces',
        src: '/gallery/practise_session.jpg',
        category: 'Training',
      },
      {
        title: 'Just another day for him',
        src: '/gallery/MSC_0429.JPG',
        category: 'Game day',
      },
      {
        title: 'The red wizards behind',
        src: '/gallery/MSC_0394.JPG',
        category: 'Team',
      },
      {
        title: 'Just another day for him',
        src: '/gallery/MSC_0324.JPG',
        category: 'Game day',
      },
      {
        title: 'MSC Sandhya Collage',
        src: '/gallery/MSC Sandhya Collage.jpg',
        category: 'Events',
      },
      {
        title: 'Gold mirmire',
        src: '/gallery/DSC05629.jpg',
        category: 'Team',
      },
      {
        title: 'Happy feet',
        src: '/gallery/DSC05585.jpg',
        category: 'Training day',
      },
      {
        title: 'Fast feet',
        src: '/gallery/DSC05571.jpg',
        category: 'Training day',
      },
      {
        title: 'Happy face',
        src: '/gallery/DSC05565.jpg',
        category: 'Training day',
      },
      {
        title: 'Nothing to stop',
        src: '/gallery/BIP_7474.JPG',
        category: 'Game day',
      },
      {
        title: 'Welcome to the team',
        src: '/gallery/BG8_9245.JPG',
        category: 'Training day',
      },
    ],
    [] // Empty dependency array means this will only be calculated once
  );

  // Simulate image loading time
  useEffect(() => {
    // Set loading true initially
    setLoading(true);

    // Create an array of image elements to track loading
    const imageElements = images.map(image => {
      const img = new Image();
      img.src = image.src;
      return img;
    });

    // Wait for all images to load or a timeout
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000); // Fallback in case images take too long

    // When all images have loaded, stop loading
    Promise.all(
      imageElements.map(img => {
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve; // Still resolve on error to continue
        });
      })
    )
      .then(() => {
        clearTimeout(timer);
        setLoading(false);
      })
      .catch(() => {
        clearTimeout(timer);
        setLoading(false);
      });

    return () => clearTimeout(timer);
  }, [images]); // Now images is stable and won't cause re-runs

  return (
    <>
      <Helmet>
        <title>Gallery - Mirmire</title>
        <meta name="description" content="Photo gallery of Mirmire events and activities" />
      </Helmet>

      {/* Loading Component */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            backdropFilter: 'blur(5px)',
          }}
        >
          <Loading />
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          bgcolor: '#000000',
          py: { xs: 6, md: 8 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            color: '#FFD700',
            fontWeight: 700,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            textAlign: 'center',
          }}
        >
          GALLERY
        </Typography>
      </Box>

      {/* Gallery Grid Section */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          py: { xs: 6, md: 8 },
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '1000px',
            px: 2,
          }}
        >
          <Grid container spacing={3}>
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                      '& .image-overlay': {
                        opacity: 1,
                      },
                    },
                  }}
                  onClick={() => setSelectedImage(image)}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      pt: '75%', // 4:3 Aspect ratio
                      overflow: 'hidden',
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={image.src}
                      alt={image.title}
                      className="gallery-image"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: '#333333',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    />
                    <Box
                      className="image-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#FFFFFF',
                          fontWeight: 600,
                          mb: 1,
                          fontSize: { xs: '0.8rem', md: '0.9rem' },
                        }}
                      >
                        {image.title}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: '#FFD700',
                          fontSize: { xs: '0.875rem', md: '0.8rem' },
                        }}
                      >
                        {image.category}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Image Modal */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90%',
            maxHeight: '90vh',
            outline: 'none',
          }}
        >
          {selectedImage && (
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
              loading="lazy"
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default Gallery;
